import type {
  AgentRuntimeActionAvailability,
  AgentRuntimeActionState,
  AgentRuntimeCapabilities,
  AgentRuntimeErrorItem,
  AgentRuntimeInteractionRequest,
  AgentRuntimeSnapshot,
  AgentRuntimeStatus,
  AgentRuntimeTimelineItem,
  AgentType,
} from "@cord/shared"
import { normalizeAgentErrorDetails } from "@cord/shared"
import type {
  NormalizedRawSyncEvent,
  RawSyncEventBatchApplyResult,
} from "@/lib/runtime-sync-batch"
import {
  asRecord,
  asString,
  createEmptyOpenCodeDerivedState,
  createOpenCodeBatchMutationState,
  isAssistantOnlyPartType,
  messageHasOpenRenderableAssistantPart,
  messageHasOpenAssistantPart,
  isTaskToolPart,
  messageContainsTaskTool,
  normalizeTimestamp,
  normalizeProviderStatus,
  resolveOpenCodeMessageOrderingTimestamp,
  shouldPromoteBusyFromAssistantPart,
  type OpenCodeBatchMutationState,
  type OpenCodeChildSessionRuntimeState,
  type OpenCodeDerivedState,
  type MessageEntry,
  type OpenCodeProjectionGraph,
  type OpenCodeTaskLinkIndexEntry,
} from "@/lib/opencode-runtime-shared"
import {
  ensureOpenCodeProjectionDerivedState,
  shouldTreatOpenStreamingTurnAsIncomplete,
  refreshOpenCodeProjectionDerivedState,
} from "@/lib/opencode-derived-state"

export type OpenCodeRawProjection = OpenCodeProjectionGraph & OpenCodeDerivedState & {
  sessionInfo: Record<string, unknown> | null
  providerStatus: Record<string, unknown> | null
  lastSessionError: { type: string; message: string } | null
  sessionErrors: AgentRuntimeErrorItem[]
  pendingPermissionsById: Record<string, Record<string, unknown>>
  pendingQuestionsById: Record<string, Record<string, unknown>>
  providerSyncBoundaryId: number | null
}

export function createEmptyOpenCodeProjection(): OpenCodeRawProjection {
  return {
    messagesById: {},
    childSessionToolPartsBySessionId: {},
    childSessionStateBySessionId: {},
    taskLinkIndexByChildSessionId: {},
    taskLinkIndexValid: true,
    sessionInfo: null,
    providerStatus: null,
    lastSessionError: null,
    sessionErrors: [],
    pendingPermissionsById: {},
    pendingQuestionsById: {},
    providerSyncBoundaryId: null,
    ...createEmptyOpenCodeDerivedState(),
  }
}

function cloneProjectionForMutation(input: OpenCodeRawProjection): OpenCodeRawProjection {
  return {
    messagesById: { ...input.messagesById },
    childSessionToolPartsBySessionId: { ...input.childSessionToolPartsBySessionId },
    childSessionStateBySessionId: { ...(input.childSessionStateBySessionId ?? {}) },
    taskLinkIndexByChildSessionId: { ...(input.taskLinkIndexByChildSessionId ?? {}) },
    taskLinkIndexValid: input.taskLinkIndexValid ?? false,
    sessionInfo: input.sessionInfo,
    providerStatus: input.providerStatus ? { ...input.providerStatus } : null,
    lastSessionError: input.lastSessionError ? { ...input.lastSessionError } : null,
    sessionErrors: [...(input.sessionErrors ?? [])],
    pendingPermissionsById: { ...input.pendingPermissionsById },
    pendingQuestionsById: { ...input.pendingQuestionsById },
    providerSyncBoundaryId: input.providerSyncBoundaryId,
    derivedStateVersion: input.derivedStateVersion,
    orderedMessageIds: input.orderedMessageIds,
    messageTimelineItemsById: input.messageTimelineItemsById,
    orderedChildSessionIds: input.orderedChildSessionIds,
    childSessionTimelineItemsBySessionId: input.childSessionTimelineItemsBySessionId,
    timeline: input.timeline,
    usageByMessageId: input.usageByMessageId,
    usage: input.usage,
  }
}

function ensureMessageInfoCreatedTimestamp(args: {
  currentInfo: Record<string, unknown> | undefined
  nextInfo: Record<string, unknown>
  partsById: MessageEntry["partsById"]
  observedAt: number
}): Record<string, unknown> {
  const nextTime = asRecord(args.nextInfo.time)
  const currentTime = asRecord(args.currentInfo?.time)
  const clonedInfo: Record<string, unknown> = {
    ...args.nextInfo,
    ...(nextTime ? { time: { ...nextTime } } : {}),
  }
  const nextCreatedAt = normalizeTimestamp(nextTime?.created)
  const nextCompletedAt = normalizeTimestamp(nextTime?.completed)
  if (nextCreatedAt !== null && nextCompletedAt !== null) return clonedInfo

  const currentCreatedAt = normalizeTimestamp(currentTime?.created)
  const currentCompletedAt = normalizeTimestamp(currentTime?.completed)
  if (currentCreatedAt !== null || currentCompletedAt !== null) {
    clonedInfo.time = {
      ...(nextTime ?? {}),
      ...(nextCreatedAt === null && currentCreatedAt !== null
        ? { created: currentCreatedAt }
        : {}),
      ...(nextCompletedAt === null && currentCompletedAt !== null
        ? { completed: currentCompletedAt }
        : {}),
    }
    return clonedInfo
  }

  const provisionalCreatedAt = resolveOpenCodeMessageOrderingTimestamp({
    message: {
      info: clonedInfo,
      partsById: args.partsById,
    },
  }) ?? args.observedAt

  clonedInfo.time = {
    ...(nextTime ?? {}),
    created: provisionalCreatedAt,
  }
  return clonedInfo
}

function normalizeSessionError(raw: unknown): { type: string; message: string } | null {
  const normalized = normalizeAgentErrorDetails({
    value: raw,
    fallbackType: "runtime_error",
  })
  if (!normalized) return null

  return {
    type: normalized.type ?? normalized.code ?? "runtime_error",
    message: normalized.message,
  }
}

function buildSessionErrorTimelineItem(args: {
  sessionId: string | null
  normalizedError: { type: string; message: string } | null
  timestamp: number
  existingCount: number
}): AgentRuntimeErrorItem {
  const message = args.normalizedError?.message ?? "Session reported an error"
  const code = args.normalizedError?.type ?? "runtime_error"

  return {
    id: `opencode:error:${args.timestamp}:${args.existingCount}`,
    kind: "error",
    message,
    code,
    timestamp: args.timestamp,
    providerRef: {
      agentType: "opencode",
      sessionId: args.sessionId ?? undefined,
    },
  }
}

function mergeChildSessionRuntimeState(args: {
  current: OpenCodeChildSessionRuntimeState | undefined
  timestamp: number
  finishReason?: string | null
  statusType?: string | null
  errorMessage?: string | null
  errorType?: string | null
}): OpenCodeChildSessionRuntimeState {
  const nextStatusType =
    typeof args.statusType === "string" && args.statusType.trim().length > 0
      ? args.statusType.trim()
      : args.current?.statusType ?? null
  const nextErrorMessage =
    typeof args.errorMessage === "string" && args.errorMessage.trim().length > 0
      ? args.errorMessage.trim()
      : args.statusType === "busy" ||
          args.statusType === "running" ||
          args.statusType === "retry" ||
          args.statusType === "working" ||
          args.statusType === "idle" ||
          args.statusType === "completed" ||
          args.statusType === "waiting_input"
        ? null
        : args.current?.errorMessage ?? null
  const nextErrorType =
    typeof args.errorType === "string" && args.errorType.trim().length > 0
      ? args.errorType.trim()
      : nextErrorMessage === null
        ? null
        : args.current?.errorType ?? null
  const nextFinishReason =
    typeof args.finishReason === "string" && args.finishReason.trim().length > 0
      ? args.finishReason.trim()
      : args.statusType === "busy" ||
          args.statusType === "running" ||
          args.statusType === "retry" ||
          args.statusType === "working"
        ? null
        : args.current?.finishReason ?? null

  return {
    statusType: nextStatusType,
    lastEventAt: args.timestamp,
    finishReason: nextFinishReason,
    errorMessage: nextErrorMessage,
    errorType: nextErrorType,
  }
}

function updateChildSessionRuntimeState(args: {
  projection: OpenCodeRawProjection
  childSessionId: string | null
  timestamp: number
  finishReason?: string | null
  statusType?: string | null
  errorMessage?: string | null
  errorType?: string | null
}): void {
  const normalizedChildSessionId = asString(args.childSessionId)
  if (!normalizedChildSessionId) return
  const current = args.projection.childSessionStateBySessionId?.[normalizedChildSessionId]
  const nextState = mergeChildSessionRuntimeState({
    current,
    timestamp: args.timestamp,
    finishReason: args.finishReason,
    statusType: args.statusType,
    errorMessage: args.errorMessage,
    errorType: args.errorType,
  })
  args.projection.childSessionStateBySessionId = {
    ...(args.projection.childSessionStateBySessionId ?? {}),
    [normalizedChildSessionId]: nextState,
  }
}

function normalizeTaskToolChildSessionStatus(rawStatus: string | null): string | null {
  if (rawStatus === "pending" || rawStatus === "running") return "running"
  if (rawStatus === "completed") return "idle"
  if (rawStatus === "error") return "error"
  return null
}

function markTaskLinkIndexDirty(
  projection: OpenCodeRawProjection,
  mutation: OpenCodeBatchMutationState,
): void {
  mutation.taskLinksDirty = true
  projection.taskLinkIndexValid = false
}

function markAllMessagesDirty(
  projection: OpenCodeRawProjection,
  mutation: OpenCodeBatchMutationState,
): void {
  for (const messageId of Object.keys(projection.messagesById)) {
    mutation.dirtyMessageIds.add(messageId)
  }
  mutation.timelineDirty = true
}

function countPendingInteractions(projection: OpenCodeRawProjection): number {
  return Object.keys(projection.pendingPermissionsById).length
    + Object.keys(projection.pendingQuestionsById).length
}

function isRunningProviderStatusType(providerStatusType: string | null): boolean {
  return providerStatusType === "busy"
    || providerStatusType === "running"
    || providerStatusType === "retry"
    || providerStatusType === "working"
}

function normalizeChildSessionStatusTypeFromProviderStatus(args: {
  providerStatus: Record<string, unknown> | null
}): string | null {
  const childStatusType = asString(args.providerStatus?.type)?.toLowerCase() ?? null
  const runtimeFinality = asRecord(args.providerStatus?.runtimeFinality)
  const runtimeFinalityState = asString(runtimeFinality?.state)?.toLowerCase() ?? null
  const finishReason =
    asString(args.providerStatus?.finishReason)?.toLowerCase() ??
    (runtimeFinalityState === "settled"
      ? asString(runtimeFinality?.reason)?.toLowerCase() ?? null
      : null)

  if (runtimeFinalityState !== "settled") {
    return childStatusType
  }

  if (childStatusType === "error" || childStatusType === "failed") {
    return childStatusType
  }

  if (finishReason === "error" || finishReason === "failed") {
    return "failed"
  }

  return childStatusType === "completed" ? "completed" : "idle"
}

function projectionHasOpenRenderableAssistantPart(projection: OpenCodeRawProjection): boolean {
  return Object.values(projection.messagesById).some((message) => messageHasOpenRenderableAssistantPart({
    partsById: message.partsById,
  }))
}

function getSettledProviderStatusObservedAt(providerStatus: Record<string, unknown> | null): number | null {
  if (!providerStatus) return null

  const providerStatusType = asString(providerStatus.type)?.toLowerCase() ?? null
  const finishedAt = normalizeTimestamp(providerStatus.finishedAt)
  const runtimeFinality = asRecord(providerStatus.runtimeFinality)
  const runtimeFinalityState = asString(runtimeFinality?.state)?.toLowerCase() ?? null
  const hasTerminalType =
    providerStatusType === "idle" ||
    providerStatusType === "error" ||
    providerStatusType === "failed" ||
    providerStatusType === "completed"

  if (finishedAt === null) return null
  return runtimeFinalityState === "settled" || hasTerminalType ? finishedAt : null
}

function isRuntimeEventOlderThanSettledProviderStatus(args: {
  providerStatus: Record<string, unknown> | null
  observedAt: number
}): boolean {
  const settledAt = getSettledProviderStatusObservedAt(args.providerStatus)
  // Runtime recovery can publish an authoritative synthetic terminal status and
  // then receive already-buffered provider frames whose timestamps are older
  // than that settlement. Those frames may update transcript details, but they
  // must not reopen the provider status or clear runtimeFinality.
  return settledAt !== null && args.observedAt <= settledAt
}

function promoteProviderStatusToBusyFromAssistantActivity(args: {
  projection: OpenCodeRawProjection
  mutation: OpenCodeBatchMutationState
  observedAt: number
}): void {
  if (isRuntimeEventOlderThanSettledProviderStatus({
    providerStatus: args.projection.providerStatus,
    observedAt: args.observedAt,
  })) {
    return
  }

  const previousShouldForceCloseOpenStreamingParts =
    shouldTreatOpenStreamingTurnAsIncomplete({
      providerStatus: args.projection.providerStatus,
      pendingInteractionCount: countPendingInteractions(args.projection),
      hasOpenRenderableAssistantPart: projectionHasOpenRenderableAssistantPart(args.projection),
    })

  const currentProviderStatusType =
    asString(args.projection.providerStatus?.type)?.toLowerCase() ?? null
  const nextProviderStatus: Record<string, unknown> = {
    ...(args.projection.providerStatus ?? {}),
    type: "busy",
    startedAt:
      isRunningProviderStatusType(currentProviderStatusType)
        ? normalizeTimestamp(args.projection.providerStatus?.startedAt) ?? args.observedAt
        : args.observedAt,
  }
  delete nextProviderStatus.finishedAt
  delete nextProviderStatus.finishReason
  delete nextProviderStatus.runtimeFinality
  delete nextProviderStatus.errorType
  delete nextProviderStatus.message

  args.projection.providerStatus = nextProviderStatus
  args.projection.lastSessionError = null

  const nextShouldForceCloseOpenStreamingParts =
    shouldTreatOpenStreamingTurnAsIncomplete({
      providerStatus: args.projection.providerStatus,
      pendingInteractionCount: countPendingInteractions(args.projection),
      hasOpenRenderableAssistantPart: projectionHasOpenRenderableAssistantPart(args.projection),
    })

  if (
    previousShouldForceCloseOpenStreamingParts
    !== nextShouldForceCloseOpenStreamingParts
  ) {
    markAllMessagesDirty(args.projection, args.mutation)
  }
}

function rebuildTaskLinkIndex(projection: OpenCodeRawProjection): void {
  const nextIndex: Record<string, OpenCodeTaskLinkIndexEntry> = {}

  const ensureEntry = (childSessionId: string): OpenCodeTaskLinkIndexEntry => {
    if (!nextIndex[childSessionId]) {
      nextIndex[childSessionId] = {
        messageIds: [],
        parentChildSessionIds: [],
      }
    }
    return nextIndex[childSessionId]
  }

  for (const [messageId, message] of Object.entries(projection.messagesById)) {
    for (const part of Object.values(message.partsById)) {
      if (part.type !== "tool" || part.tool !== "task") continue
      const state = asRecord(part.state)
      const metadata = asRecord(state?.metadata)
      const childSessionId = asString(metadata?.sessionId)
      if (!childSessionId) continue
      const entry = ensureEntry(childSessionId)
      if (!entry.messageIds.includes(messageId)) {
        entry.messageIds.push(messageId)
      }
    }
  }

  for (const [parentChildSessionId, partsById] of Object.entries(projection.childSessionToolPartsBySessionId)) {
    for (const part of Object.values(partsById)) {
      if (part.type !== "tool" || part.tool !== "task") continue
      const state = asRecord(part.state)
      const metadata = asRecord(state?.metadata)
      const childSessionId = asString(metadata?.sessionId)
      if (!childSessionId) continue
      const entry = ensureEntry(childSessionId)
      if (!entry.parentChildSessionIds.includes(parentChildSessionId)) {
        entry.parentChildSessionIds.push(parentChildSessionId)
      }
    }
  }

  projection.taskLinkIndexByChildSessionId = nextIndex
  projection.taskLinkIndexValid = true
}

function markItemsLinkedToChildSessionDirty(args: {
  projection: OpenCodeRawProjection
  mutation: OpenCodeBatchMutationState
  childSessionId: string | null
}): void {
  const normalizedChildSessionId = asString(args.childSessionId)
  if (!normalizedChildSessionId) return

  if (args.projection.taskLinkIndexValid !== true) {
    rebuildTaskLinkIndex(args.projection)
  }

  const linkedEntry = args.projection.taskLinkIndexByChildSessionId?.[normalizedChildSessionId]
  if (!linkedEntry) return

  for (const messageId of linkedEntry.messageIds) {
    args.mutation.dirtyMessageIds.add(messageId)
  }

  for (const sessionId of linkedEntry.parentChildSessionIds) {
    args.mutation.dirtyChildSessionIds.add(sessionId)
  }
}

function applyIncrementalOpenCodeEventMutable(
  next: OpenCodeRawProjection,
  mutation: OpenCodeBatchMutationState,
  eventType: string,
  payload: Record<string, unknown>,
  childSessionId?: string | null,
  eventTimestamp?: number,
): void {
  const props = asRecord(payload.properties)
  const normalizedChildSessionId = asString(childSessionId)
  const isChildEvent = normalizedChildSessionId !== null
  const observedAt = normalizeTimestamp(eventTimestamp) ?? Date.now()

  if (isChildEvent) {
    updateChildSessionRuntimeState({
      projection: next,
      childSessionId: normalizedChildSessionId,
      timestamp: observedAt,
    })
    if (normalizedChildSessionId) {
      mutation.dirtyChildSessionIds.add(normalizedChildSessionId)
    }
    markItemsLinkedToChildSessionDirty({
      projection: next,
      mutation,
      childSessionId: normalizedChildSessionId,
    })
  }

  switch (eventType) {
    case "message.updated": {
      if (isChildEvent) return
      const info = asRecord(props?.info)
      const messageId = asString(info?.id)
      if (!info || !messageId) return
      const current = next.messagesById[messageId]
      const nextInfo = ensureMessageInfoCreatedTimestamp({
        currentInfo: current?.info,
        nextInfo: info,
        partsById: current?.partsById ?? {},
        observedAt,
      })
      if (
        nextInfo.role === "assistant"
        && normalizeTimestamp(asRecord(nextInfo.time)?.completed) === null
        && current
        && messageHasOpenAssistantPart(current.partsById)
      ) {
        promoteProviderStatusToBusyFromAssistantActivity({
          projection: next,
          mutation,
          observedAt,
        })
      }
      mutation.dirtyMessageIds.add(messageId)
      mutation.messageOrderDirty = true
      mutation.usageDirty = true
      next.messagesById[messageId] = {
        info: nextInfo,
        partsById: current ? current.partsById : {},
      }
      return
    }
    case "message.removed": {
      const messageId = asString(props?.messageID)
      if (!messageId) return
      if (normalizedChildSessionId) {
        const existingParts = next.childSessionToolPartsBySessionId[normalizedChildSessionId]
        if (!existingParts) return
        mutation.dirtyChildSessionIds.add(normalizedChildSessionId)
        const nextParts = Object.fromEntries(
          Object.entries(existingParts).filter(([, part]: [string, Record<string, unknown>]) =>
            asString(part.messageID) !== messageId
          ),
        )
        if (Object.keys(nextParts).length !== Object.keys(existingParts).length) {
          markTaskLinkIndexDirty(next, mutation)
        }
        if (Object.keys(nextParts).length === 0) {
          delete next.childSessionToolPartsBySessionId[normalizedChildSessionId]
          mutation.childSessionOrderDirty = true
        } else {
          next.childSessionToolPartsBySessionId[normalizedChildSessionId] = nextParts
        }
        return
      }
      const current = next.messagesById[messageId]
      mutation.dirtyMessageIds.add(messageId)
      mutation.messageOrderDirty = true
      mutation.usageDirty = true
      if (messageContainsTaskTool(current)) {
        markTaskLinkIndexDirty(next, mutation)
      }
      delete next.messagesById[messageId]
      return
    }
    case "message.part.updated": {
      const part = asRecord(props?.part)
      if (isChildEvent && part?.type !== "tool") return
      const messageId = asString(part?.messageID)
      const partId = asString(part?.id)
      if (!part || !messageId || !partId) return
      if (normalizedChildSessionId) {
        const existingParts = next.childSessionToolPartsBySessionId[normalizedChildSessionId] ?? {}
        const previousPart = existingParts[partId]
        mutation.dirtyChildSessionIds.add(normalizedChildSessionId)
        if (!previousPart) {
          mutation.childSessionOrderDirty = !(normalizedChildSessionId in next.childSessionToolPartsBySessionId)
        }
        if (isTaskToolPart(part) || isTaskToolPart(previousPart)) {
          markTaskLinkIndexDirty(next, mutation)
        }
        if (isTaskToolPart(part)) {
          const state = asRecord(part.state)
          const metadata = asRecord(state?.metadata)
          updateChildSessionRuntimeState({
            projection: next,
            childSessionId: asString(metadata?.sessionId),
            timestamp: observedAt,
            statusType: normalizeTaskToolChildSessionStatus(asString(state?.status)),
          })
        }
        next.childSessionToolPartsBySessionId[normalizedChildSessionId] = {
          ...existingParts,
          [partId]: { ...part },
        }
        return
      }
      const current = next.messagesById[messageId]
      const partType = asString(part.type)?.toLowerCase() ?? null
      const messageCompletedAt = normalizeTimestamp(asRecord(current?.info?.time)?.completed)
      const shouldTreatPartAsAssistantActivity =
        partType === "text"
          ? current?.info?.role === "assistant" || current?.info == null
          : current?.info?.role === "assistant"
            || (current?.info == null && isAssistantOnlyPartType(partType))
      if (
        messageCompletedAt === null
        && shouldTreatPartAsAssistantActivity
        && shouldPromoteBusyFromAssistantPart(part)
      ) {
        promoteProviderStatusToBusyFromAssistantActivity({
          projection: next,
          mutation,
          observedAt,
        })
      }
      const previousOrderingTimestamp = current
        ? resolveOpenCodeMessageOrderingTimestamp({ message: current })
        : null
      mutation.dirtyMessageIds.add(messageId)
      mutation.usageDirty = true
      const partsById = current ? { ...current.partsById } : {}
      const previousPart = partsById[partId]
      if (isTaskToolPart(part) || isTaskToolPart(previousPart)) {
        markTaskLinkIndexDirty(next, mutation)
      }
      if (isTaskToolPart(part)) {
        const state = asRecord(part.state)
        const metadata = asRecord(state?.metadata)
        updateChildSessionRuntimeState({
          projection: next,
          childSessionId: asString(metadata?.sessionId),
          timestamp: observedAt,
          statusType: normalizeTaskToolChildSessionStatus(asString(state?.status)),
        })
      }
      partsById[partId] = { ...part }
      const nextMessage: MessageEntry = {
        info: ensureMessageInfoCreatedTimestamp({
          currentInfo: current?.info,
          nextInfo: current?.info ?? {
            id: messageId,
            sessionID: asString(part.sessionID) ?? childSessionId ?? "",
          },
          partsById,
          observedAt,
        }),
        partsById,
      }
      const nextOrderingTimestamp = resolveOpenCodeMessageOrderingTimestamp({
        message: nextMessage,
      })
      if (!current || previousOrderingTimestamp !== nextOrderingTimestamp) {
        mutation.messageOrderDirty = true
      }
      next.messagesById[messageId] = {
        ...nextMessage,
      }
      return
    }
    case "message.part.delta": {
      if (isChildEvent) return
      const messageId = asString(props?.messageID)
      const partId = asString(props?.partID)
      const field = asString(props?.field)
      const delta = typeof props?.delta === "string" ? props.delta : null
      if (!messageId || !partId || !field || delta === null) return
      const current = next.messagesById[messageId]
      if (!current) return
      const part = current.partsById[partId]
      if (!part) return
      if (
        current.info?.role === "assistant"
        && normalizeTimestamp(asRecord(current.info?.time)?.completed) === null
        && shouldPromoteBusyFromAssistantPart(part)
      ) {
        promoteProviderStatusToBusyFromAssistantActivity({
          projection: next,
          mutation,
          observedAt,
        })
      }
      mutation.dirtyMessageIds.add(messageId)
      if (isTaskToolPart(part)) {
        markTaskLinkIndexDirty(next, mutation)
      }
      const currentValue = typeof part[field] === "string" ? (part[field] as string) : ""
      next.messagesById[messageId] = {
        ...current,
        partsById: {
          ...current.partsById,
          [partId]: {
            ...part,
            [field]: currentValue + delta,
          },
        },
      }
      return
    }
    case "message.part.removed": {
      const messageId = asString(props?.messageID)
      const partId = asString(props?.partID)
      if (!messageId || !partId) return
      if (normalizedChildSessionId) {
        const existingParts = next.childSessionToolPartsBySessionId[normalizedChildSessionId]
        if (!existingParts) return
        const part = existingParts[partId]
        if (!part || asString(part.messageID) !== messageId) return
        mutation.dirtyChildSessionIds.add(normalizedChildSessionId)
        if (isTaskToolPart(part)) {
          markTaskLinkIndexDirty(next, mutation)
        }
        const nextParts = { ...existingParts }
        delete nextParts[partId]
        if (Object.keys(nextParts).length === 0) {
          delete next.childSessionToolPartsBySessionId[normalizedChildSessionId]
          mutation.childSessionOrderDirty = true
        } else {
          next.childSessionToolPartsBySessionId[normalizedChildSessionId] = nextParts
        }
        return
      }
      const current = next.messagesById[messageId]
      if (!current) return
      const part = current.partsById[partId]
      mutation.dirtyMessageIds.add(messageId)
      mutation.usageDirty = true
      if (isTaskToolPart(part)) {
        markTaskLinkIndexDirty(next, mutation)
      }
      const partsById = { ...current.partsById }
      delete partsById[partId]
      next.messagesById[messageId] = {
        ...current,
        partsById,
      }
      return
    }
    case "session.updated": {
      if (isChildEvent) return
      next.sessionInfo = asRecord(props?.info)
      return
    }
    case "session.status": {
      if (isChildEvent) {
        if (isRuntimeEventOlderThanSettledProviderStatus({
          providerStatus: next.providerStatus,
          observedAt,
        })) {
          return
        }
        const childProviderStatus = normalizeProviderStatus(props?.status)
        const childRuntimeFinality = asRecord(childProviderStatus?.runtimeFinality)
        const childRuntimeFinalityState = asString(childRuntimeFinality?.state)?.toLowerCase() ?? null
        const childFinishReason =
          asString(childProviderStatus?.finishReason) ??
          (childRuntimeFinalityState === "settled"
            ? asString(childRuntimeFinality?.reason)
            : null)
        updateChildSessionRuntimeState({
          projection: next,
          childSessionId: normalizedChildSessionId,
          timestamp: observedAt,
          finishReason: childFinishReason,
          statusType: normalizeChildSessionStatusTypeFromProviderStatus({
            providerStatus: childProviderStatus,
          }),
        })
        return
      }
      const previousProviderStatus = next.providerStatus
      const previousProviderStatusTerminalDerivationKey =
        buildProviderStatusTerminalDerivationKey(previousProviderStatus)
      const previousShouldForceCloseOpenStreamingParts =
        shouldTreatOpenStreamingTurnAsIncomplete({
          providerStatus: previousProviderStatus,
          pendingInteractionCount: countPendingInteractions(next),
          hasOpenRenderableAssistantPart: projectionHasOpenRenderableAssistantPart(next),
        })
      const nextProviderStatus = normalizeProviderStatus(props?.status)
      // Defensive: audit-synthetic events emitted by older servers (pre the
      // matching runtime-sync.ts enrichment) carry only {type: "idle" | "error"}
      // with no terminal markers. The client's transcript projection refuses
      // to flip idle in that case and the "Thinking" shimmer / Stop button
      // stay stuck forever. We identify such events via the synthetic source
      // tag the DO writes and inject the terminal markers they should have
      // carried. Safe because we only touch events explicitly tagged as
      // coming from the semantic_running_audit recovery path.
      applyAuditSyntheticTerminalMarkersIfNeeded({
        providerStatus: nextProviderStatus,
        properties: props,
        observedAt,
      })
      next.providerStatus = mergeProviderStatusWithControlPlaneStatus({
        currentProviderStatus: next.providerStatus,
        nextProviderStatus,
        observedAt,
      })
      const nextShouldForceCloseOpenStreamingParts =
        shouldTreatOpenStreamingTurnAsIncomplete({
          providerStatus: next.providerStatus,
          pendingInteractionCount: countPendingInteractions(next),
          hasOpenRenderableAssistantPart: projectionHasOpenRenderableAssistantPart(next),
        })
      const nextProviderStatusTerminalDerivationKey =
        buildProviderStatusTerminalDerivationKey(next.providerStatus)
      if (
        previousShouldForceCloseOpenStreamingParts !==
          nextShouldForceCloseOpenStreamingParts ||
        previousProviderStatusTerminalDerivationKey !==
          nextProviderStatusTerminalDerivationKey
      ) {
        markAllMessagesDirty(next, mutation)
      }
      const providerStatusType = asString(next.providerStatus?.type)?.toLowerCase()
      if (
        providerStatusType === "busy" ||
        providerStatusType === "running" ||
        providerStatusType === "idle" ||
        providerStatusType === "waiting_input" ||
        providerStatusType === "retry" ||
        providerStatusType === "working"
      ) {
        next.lastSessionError = null
      }
      return
    }
    case "session.error": {
      if (isChildEvent) {
        if (isRuntimeEventOlderThanSettledProviderStatus({
          providerStatus: next.providerStatus,
          observedAt,
        })) {
          return
        }
        const normalizedError = normalizeSessionError(props?.error)
        updateChildSessionRuntimeState({
          projection: next,
          childSessionId: normalizedChildSessionId,
          timestamp: observedAt,
          statusType: "error",
          errorMessage: normalizedError?.message ?? "Session reported an error",
          errorType: normalizedError?.type ?? "runtime_error",
        })
        return
      }
      if (isRuntimeEventOlderThanSettledProviderStatus({
        providerStatus: next.providerStatus,
        observedAt,
      })) {
        return
      }
      const previousShouldForceCloseOpenStreamingParts =
        shouldTreatOpenStreamingTurnAsIncomplete({
          providerStatus: next.providerStatus,
          pendingInteractionCount: countPendingInteractions(next),
          hasOpenRenderableAssistantPart: projectionHasOpenRenderableAssistantPart(next),
        })
      const normalizedError = normalizeSessionError(props?.error)
      const nextSessionError = buildSessionErrorTimelineItem({
        sessionId: asString(props?.sessionID),
        normalizedError,
        timestamp: observedAt,
        existingCount: next.sessionErrors?.length ?? 0,
      })
      const existingSessionErrors = next.sessionErrors ?? []
      const previousSessionError = existingSessionErrors[existingSessionErrors.length - 1]
      next.providerStatus = mergeProviderStatusWithControlPlaneStatus({
        currentProviderStatus: next.providerStatus,
        nextProviderStatus: {
          type: "error",
          ...(normalizedError
            ? {
                message: normalizedError.message,
                errorType: normalizedError.type,
              }
            : {}),
        },
        observedAt,
      })
      const nextShouldForceCloseOpenStreamingParts =
        shouldTreatOpenStreamingTurnAsIncomplete({
          providerStatus: next.providerStatus,
          pendingInteractionCount: countPendingInteractions(next),
          hasOpenRenderableAssistantPart: projectionHasOpenRenderableAssistantPart(next),
        })
      if (
        previousShouldForceCloseOpenStreamingParts !==
          nextShouldForceCloseOpenStreamingParts
      ) {
        markAllMessagesDirty(next, mutation)
      }
      next.lastSessionError =
        normalizedError ?? { type: "runtime_error", message: "Session reported an error" }
      if (
        !previousSessionError ||
        previousSessionError.timestamp !== nextSessionError.timestamp ||
        previousSessionError.message !== nextSessionError.message ||
        previousSessionError.code !== nextSessionError.code
      ) {
        next.sessionErrors = [...existingSessionErrors, nextSessionError]
        mutation.timelineDirty = true
      }
      return
    }
    case "permission.asked": {
      const requestId = asString(props?.id) ?? asString(props?.requestId)
      if (!requestId) return
      next.pendingPermissionsById[requestId] = props ? { ...props } : {}
      markAllMessagesDirty(next, mutation)
      return
    }
    case "permission.replied": {
      const requestId = asString(props?.requestID) ?? asString(props?.requestId)
      if (!requestId) return
      delete next.pendingPermissionsById[requestId]
      markAllMessagesDirty(next, mutation)
      return
    }
    case "question.asked": {
      const requestId = asString(props?.id) ?? asString(props?.requestID) ?? asString(props?.requestId)
      if (!requestId) return
      next.pendingQuestionsById[requestId] = props ? { ...props } : {}
      markAllMessagesDirty(next, mutation)
      return
    }
    case "question.replied":
    case "question.rejected": {
      const requestId = asString(props?.requestID) ?? asString(props?.requestId)
      if (!requestId) return
      delete next.pendingQuestionsById[requestId]
      markAllMessagesDirty(next, mutation)
      return
    }
    default:
      return
  }
}

function buildPendingInteractions(
  projection: OpenCodeRawProjection,
): AgentRuntimeInteractionRequest[] {
  const interactions: AgentRuntimeInteractionRequest[] = []

  for (const permission of Object.values(projection.pendingPermissionsById)) {
    const requestId = asString(permission.id) ?? asString(permission.requestId)
    if (!requestId) continue
    interactions.push({
      kind: "permission",
      requestId,
      permission: asString(permission.permission) ?? undefined,
      patterns: Array.isArray(permission.patterns)
        ? permission.patterns.map((entry) => String(entry))
        : undefined,
      raw: permission,
      providerRef: {
        agentType: "opencode",
        sessionId: asString(permission.sessionID) ?? undefined,
      },
    })
  }

  for (const question of Object.values(projection.pendingQuestionsById)) {
    const requestId = asString(question.id) ?? asString(question.requestID) ?? asString(question.requestId)
    if (!requestId) continue
    const providerTool = asRecord(question.tool)
    const prompts = Array.isArray(question.questions)
      ? question.questions
          .map((entry) => asRecord(entry))
          .filter((entry): entry is Record<string, unknown> => entry !== null)
          .map((entry, index) => ({
            id: `${requestId}:${index}`,
            prompt: asString(entry.question) ?? "",
            header: asString(entry.header) ?? undefined,
            allowMultiple: entry.multiple === true,
            allowCustom: entry.custom === true,
            options: Array.isArray(entry.options)
              ? entry.options
                  .map((opt) => asRecord(opt))
                  .filter((opt): opt is Record<string, unknown> => opt !== null)
                  .map((opt, optionIndex) => ({
                    id: `${requestId}:${index}:${optionIndex}`,
                    label: asString(opt.label) ?? "",
                    description: asString(opt.description) ?? undefined,
                  }))
              : [],
          }))
      : []

    interactions.push({
      kind: "question",
      requestId,
      prompts,
      raw: question,
      providerRef: {
        agentType: "opencode",
        sessionId: asString(question.sessionID) ?? undefined,
        messageId: asString(providerTool?.messageID) ?? undefined,
        toolCallId: asString(providerTool?.callID) ?? undefined,
      },
    })
  }

  return interactions
}

function buildStatus(
  providerStatus: Record<string, unknown> | null,
  pendingInteractionCount: number,
  lastSessionError: { type: string; message: string } | null,
  hasStreamingTimelineActivity: boolean,
): AgentRuntimeStatus {
  const rawType = asString(providerStatus?.type)?.toLowerCase() ?? null
  const finishedAt = normalizeTimestamp(providerStatus?.finishedAt) ?? null
  const replicationIntegrity = asRecord(providerStatus?.replicationIntegrity)
  const replicationIntegrityState = asString(replicationIntegrity?.state)
  const runtimeFinality = asRecord(providerStatus?.runtimeFinality)
  const runtimeFinalityState = asString(runtimeFinality?.state)
  const runtimeFinalityReason =
    runtimeFinalityState === "settled" ? asString(runtimeFinality?.reason) : null
  const finishedAtSupportsTerminalStatus =
    finishedAt !== null && (
      rawType === "idle" ||
      rawType === "error" ||
      rawType === "failed" ||
      rawType === "completed"
    )
  const runtimeHasTerminalFinality =
    finishedAtSupportsTerminalStatus || runtimeFinalityState === "settled" || rawType === "completed"
  let phase: AgentRuntimeStatus["phase"] = "idle"
  if (rawType === "error" || rawType === "failed") {
    phase = "error"
  } else if (pendingInteractionCount > 0) {
    phase = "waiting_input"
  } else if (rawType === "waiting_input") {
    phase = "waiting_input"
  } else if (rawType === "busy" || rawType === "running" || rawType === "retry" || rawType === "working") {
    phase = runtimeHasTerminalFinality ? "idle" : "running"
  } else if (rawType === "idle" && hasStreamingTimelineActivity) {
    phase = runtimeHasTerminalFinality ? "idle" : "running"
  } else if (rawType === null && hasStreamingTimelineActivity) {
    phase = runtimeHasTerminalFinality ? "idle" : "running"
  }

  const startedAt = normalizeTimestamp(providerStatus?.startedAt) ?? null
  const persistedErrorMessage = asString(lastSessionError?.message)
  const persistedErrorType = asString(lastSessionError?.type) ?? "runtime_error"

  return {
    phase,
    agentType: "opencode",
    startedAt,
    finishedAt,
    finishReason:
      asString(providerStatus?.finishReason) ??
      runtimeFinalityReason ??
      (phase === "waiting_input" ? "waiting_input" : phase === "idle" ? "completed" : null),
    error: phase === "error"
      ? {
          type:
            asString(providerStatus?.errorType) ??
            persistedErrorType,
          message:
            asString(providerStatus?.message) ??
            persistedErrorMessage ??
            "Session is in an error state",
        }
      : persistedErrorMessage
        ? {
            type: persistedErrorType,
            message: persistedErrorMessage,
          }
        : null,
    replicationIntegrity:
      replicationIntegrityState === "healthy" ||
      replicationIntegrityState === "suspect" ||
      replicationIntegrityState === "broken"
        ? {
            state: replicationIntegrityState,
            reason: asString(replicationIntegrity?.reason),
            detectedAt: normalizeTimestamp(replicationIntegrity?.detectedAt),
          }
        : null,
    runtimeFinality:
      runtimeFinalityState === "live" ||
      runtimeFinalityState === "settled" ||
      runtimeFinalityState === "unknown"
        ? {
            state: runtimeFinalityState,
            reason: asString(runtimeFinality?.reason),
          }
        : null,
  }
}

// Tag the SessionDO writes onto synthetic session.status events emitted by the
// semantic_running_audit recovery path. Kept in sync with
// apps/api/src/do/session/runtime-sync.ts.
const AUDIT_SYNTHETIC_SOURCE = "semantic_running_audit"

// Fallback finish reason used when the audit path has no persisted
// last_finish_reason to carry forward. Makes the origin explicit for
// logging and downstream classification.
const AUDIT_SETTLED_FALLBACK_REASON = "audit_settled"

// Defensive client-side normalization for audit-synthetic session.status
// events. Mutates the provider status in place to inject `finishedAt` and a
// `settled` `runtimeFinality` if they are missing. The server already emits
// these markers after the fix in runtime-sync.ts; this layer handles two
// backwards-compatibility cases:
//   1. Events persisted in the durable replica before the server fix rolled
//      out (they will replay through this path on hydrate).
//   2. Any older server build still sending the legacy minimal payload.
//
// Only applied to events explicitly tagged with
// `__cordSynthetic: true` and `__cordSyntheticSource: "semantic_running_audit"`
// so normal provider status events (which legitimately stay non-terminal) are
// untouched.
function applyAuditSyntheticTerminalMarkersIfNeeded(args: {
  providerStatus: Record<string, unknown> | null
  properties: Record<string, unknown> | null
  observedAt: number
}): void {
  if (!args.providerStatus || !args.properties) return
  const isSynthetic = args.properties.__cordSynthetic === true
  const syntheticSource = asString(args.properties.__cordSyntheticSource)
  if (!isSynthetic || syntheticSource !== AUDIT_SYNTHETIC_SOURCE) return

  const providerStatusType = asString(args.providerStatus.type)?.toLowerCase() ?? null
  // The audit only emits `idle` or `error`. Guard anyway: never force-close
  // a genuinely running-ish status even if it was mis-tagged as synthetic.
  if (
    providerStatusType !== "idle" &&
    providerStatusType !== "error" &&
    providerStatusType !== "failed" &&
    providerStatusType !== "completed"
  ) {
    return
  }

  if (normalizeTimestamp(args.providerStatus.finishedAt) === null) {
    args.providerStatus.finishedAt = args.observedAt
  }

  const existingFinality = asRecord(args.providerStatus.runtimeFinality)
  const existingFinalityState = asString(existingFinality?.state)?.toLowerCase() ?? null
  if (existingFinalityState !== "settled") {
    const existingReason = asString(existingFinality?.reason)
    const derivedReason =
      existingReason ??
      asString(args.providerStatus.finishReason) ??
      (providerStatusType === "error" || providerStatusType === "failed"
        ? "error"
        : AUDIT_SETTLED_FALLBACK_REASON)
    args.providerStatus.runtimeFinality = {
      state: "settled",
      reason: derivedReason,
    }
  }

  if (asString(args.providerStatus.finishReason) === null) {
    const finalityReason = asString(asRecord(args.providerStatus.runtimeFinality)?.reason)
    if (finalityReason) {
      args.providerStatus.finishReason = finalityReason
    }
  }
}

function buildProviderStatusTerminalDerivationKey(
  providerStatus: Record<string, unknown> | null,
): string {
  const providerStatusType = asString(providerStatus?.type)?.toLowerCase() ?? null
  const finishedAt = normalizeTimestamp(providerStatus?.finishedAt)
  const finishReason = asString(providerStatus?.finishReason)?.toLowerCase() ?? null
  const runtimeFinality = asRecord(providerStatus?.runtimeFinality)
  const runtimeFinalityState = asString(runtimeFinality?.state)?.toLowerCase() ?? null
  const runtimeFinalityReason = asString(runtimeFinality?.reason)?.toLowerCase() ?? null
  const terminalByType =
    providerStatusType === "error" ||
    providerStatusType === "failed" ||
    providerStatusType === "completed"
  const terminalByFinishedAt = finishedAt !== null && (
    providerStatusType === "idle" ||
    providerStatusType === "error" ||
    providerStatusType === "failed" ||
    providerStatusType === "completed"
  )
  const terminalByFinality = runtimeFinalityState === "settled"

  return JSON.stringify({
    finishedAt,
    finishReason,
    providerStatusType,
    runtimeFinalityReason,
    runtimeFinalityState,
    terminal: terminalByType || terminalByFinishedAt || terminalByFinality,
  })
}

function mergeProviderStatusWithControlPlaneStatus(args: {
  currentProviderStatus: Record<string, unknown> | null
  nextProviderStatus: Record<string, unknown> | null
  observedAt: number
}): Record<string, unknown> | null {
  if (!args.nextProviderStatus) return args.nextProviderStatus

  const nextProviderStatusType = asString(args.nextProviderStatus.type)?.toLowerCase() ?? null
  const replicationIntegrity = asRecord(args.currentProviderStatus?.replicationIntegrity)
  const currentRuntimeFinality = asRecord(args.currentProviderStatus?.runtimeFinality)
  const currentRuntimeFinalityState = asString(currentRuntimeFinality?.state)
  const nextRuntimeFinality = asRecord(args.nextProviderStatus.runtimeFinality)

  if (
    isRuntimeEventOlderThanSettledProviderStatus({
      providerStatus: args.currentProviderStatus,
      observedAt: args.observedAt,
    })
  ) {
    return args.currentProviderStatus
  }

  // Resolve the effective runtimeFinality for the merged status. Priority:
  //   1. If the next event carries an explicit `runtimeFinality`, use it.
  //      Events that ship their own finality (e.g. the audit-synthetic
  //      session.status emitted by the SessionDO when it corrects a stale
  //      running state) are authoritative about the moment of the event and
  //      must not be clobbered by a stale current finality (which could be
  //      `live` left over from a prior control-plane snapshot that has not
  //      been re-delivered yet).
  //   2. Else if the next event is a running-ish status overtaking a
  //      currently-`settled` finality, clear the finality — a new running
  //      cycle invalidates the previous terminal marker.
  //   3. Else preserve the current finality. Typical session.status events
  //      don't carry finality, so the control-plane-sourced finality (or a
  //      prior audit-synthetic finality) should remain authoritative.
  const resolvedRuntimeFinality = nextRuntimeFinality !== null
    ? nextRuntimeFinality
    : (nextProviderStatusType === "busy"
      || nextProviderStatusType === "running"
      || nextProviderStatusType === "retry"
      || nextProviderStatusType === "working")
      && currentRuntimeFinalityState === "settled"
      ? null
      : currentRuntimeFinality

  if (!replicationIntegrity && !resolvedRuntimeFinality) {
    return args.nextProviderStatus
  }

  return {
    ...args.nextProviderStatus,
    ...(replicationIntegrity ? { replicationIntegrity } : {}),
    ...(resolvedRuntimeFinality ? { runtimeFinality: resolvedRuntimeFinality } : {}),
  }
}

function availability(enabled: boolean, disabledReason: string | null): AgentRuntimeActionAvailability {
  return {
    available: true,
    enabled,
    disabledReason: enabled ? null : disabledReason,
  }
}

function buildActionState(
  status: AgentRuntimeStatus,
  timeline: AgentRuntimeTimelineItem[],
  pendingInteractions: AgentRuntimeInteractionRequest[],
  sessionInfo: Record<string, unknown> | null,
): AgentRuntimeActionState {
  const busy = status.phase === "running"
  const hasConversation = timeline.length > 0
  const hasPendingPermission = pendingInteractions.some((entry) => entry.kind === "permission")
  const hasPendingQuestion = pendingInteractions.some((entry) => entry.kind === "question")
  const revertMessageId = asString(asRecord(sessionInfo?.revert)?.messageID)
  const busyReason = "Unavailable while agent is running"
  const noConversationReason = "No conversation yet"

  return {
    availability: {
      abort: availability(busy, "Nothing to abort right now"),
      undo: availability(!busy && hasConversation, busy ? busyReason : noConversationReason),
      redo: availability(!busy && Boolean(revertMessageId), busy ? busyReason : "No redo checkpoint available"),
      compact: availability(!busy, busy ? busyReason : null),
      fork_message: availability(!busy && hasConversation, busy ? busyReason : noConversationReason),
      edit_message: availability(!busy && hasConversation, busy ? busyReason : noConversationReason),
      reply_permission: availability(hasPendingPermission, "No pending permission request"),
      reply_question: availability(hasPendingQuestion, "No pending question"),
      question_reject: availability(hasPendingQuestion, "No pending question"),
    },
    revertMessageId,
  }
}

export function buildOpenCodeSnapshot(
  projection: OpenCodeRawProjection,
  eventId: number,
): AgentRuntimeSnapshot {
  const normalizedProjection = ensureOpenCodeProjectionDerivedState(projection)
  const pendingInteractions = buildPendingInteractions(normalizedProjection)
  const timeline = normalizedProjection.timeline
  const hasStreamingAssistantText = timeline.some((item) => (
    item.kind === "assistant_text"
    && item.streaming
    && typeof item.messageId === "string"
    && normalizedProjection.messagesById[item.messageId] != null
    && normalizedProjection.messagesById[item.messageId]?.info?.role !== "user"
  ))
  const status = buildStatus(
    normalizedProjection.providerStatus,
    pendingInteractions.length,
    normalizedProjection.lastSessionError,
    timeline.some((item) => (
      item.kind === "thinking" && item.streaming
    ) || (
      item.kind === "tool_call" && (item.status === "pending" || item.status === "running")
    )) || hasStreamingAssistantText,
  )

  return {
    timeline,
    pendingMessages: [],
    pendingInteractions,
    status,
    usage: normalizedProjection.usage,
    actions: buildActionState(status, timeline, pendingInteractions, normalizedProjection.sessionInfo),
    activeEditOperation: null,
    conversationEpoch: 0,
    sessionActionOutcome: null,
    capabilities: buildCapabilities("opencode"),
    cursorState: {
      providerEventId: eventId,
      canonicalEventId: null,
      providerSyncBoundaryId: normalizedProjection.providerSyncBoundaryId,
    },
  }
}

function buildCapabilities(agentType: AgentType): AgentRuntimeCapabilities {
  return {
    agentType,
    supportsPromptParts: true,
    supportsImageAttachments: true,
    supportsSlashCommands: true,
    supportsSessionActions: true,
    supportsQuestions: true,
    supportsPermissions: true,
    supportsUsage: true,
    supportsForkFromMessage: true,
    supportsEditFromMessage: true,
    supportsSubagents: true,
  }
}

const OPENCODE_INCREMENTAL_EVENT_TYPES = new Set([
  "message.updated",
  "message.part.delta",
  "message.part.updated",
  "message.removed",
  "message.part.removed",
  "session.updated",
  "session.status",
  "session.error",
  "permission.asked",
  "permission.replied",
  "question.asked",
  "question.replied",
  "question.rejected",
])

export function applyOpenCodeRawSyncEvent(input: {
  projection: OpenCodeRawProjection | null
  eventType: string
  payload: unknown
  eventId: number
  childSessionId?: string | null
  timestamp: number
}): { projection: OpenCodeRawProjection; snapshot: AgentRuntimeSnapshot } | null {
  const applied = applyOpenCodeRawSyncEventsBatch({
    projection: input.projection,
    events: [{
      eventType: input.eventType,
      payload: input.payload,
      eventId: input.eventId,
      childSessionId: input.childSessionId ?? null,
      timestamp: input.timestamp,
    }],
  })
  if (!applied) return null
  return {
    projection: applied.projection,
    snapshot: applied.snapshot,
  }
}

export function applyOpenCodeRawSyncEventsBatch(input: {
  projection: OpenCodeRawProjection | null
  events: NormalizedRawSyncEvent[]
}): RawSyncEventBatchApplyResult<OpenCodeRawProjection> | null {
  const baseProjection = ensureOpenCodeProjectionDerivedState(
    input.projection ?? createEmptyOpenCodeProjection(),
  )
  let nextProjection: OpenCodeRawProjection | null = null
  let appliedCount = 0
  let lastAppliedEventId = 0
  let lastAppliedTimestamp = 0
  const mutation = createOpenCodeBatchMutationState()

  for (const event of input.events) {
    const eventId = Number.isFinite(event.eventId) && event.eventId > 0
      ? Math.floor(event.eventId)
      : 0
    if (eventId <= 0) continue
    if (!OPENCODE_INCREMENTAL_EVENT_TYPES.has(event.eventType)) continue

    const payloadRecord = asRecord(event.payload)
    if (!payloadRecord) continue

    if (nextProjection === null) {
      nextProjection = cloneProjectionForMutation(baseProjection)
    }

    applyIncrementalOpenCodeEventMutable(
      nextProjection,
      mutation,
      event.eventType,
      payloadRecord,
      event.childSessionId,
      event.timestamp,
    )
    appliedCount += 1
    lastAppliedEventId = eventId
    lastAppliedTimestamp = event.timestamp
  }

  if (appliedCount === 0) return null

  if (nextProjection === null) return null

  refreshOpenCodeProjectionDerivedState(nextProjection, mutation)

  const snapshot = buildOpenCodeSnapshot(nextProjection, lastAppliedEventId)

  return {
    projection: nextProjection,
    snapshot,
    appliedCount,
    lastAppliedEventId,
    lastAppliedTimestamp,
  }
}
